import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '@/components/ui/tooltip'
import { Button } from '@/components/ui/button'
import { GameRoomStatus, Room } from '@/redux/types/room'
import { useAppDispatch } from '@/redux/hooks'
import { getRoomDetails, updateRoomDetails } from '@/redux/asyncThunks/room'
import { toast } from 'sonner'
import {
  startNextRoundThunk,
  fetchTimerConfigurationThunk,
} from '@/redux/asyncThunks/timerConfiguration'
import { useState, useCallback, useMemo } from 'react'
import { TimerMode } from '@/components/settings/timer-configuration/types'
import { cn } from '@/lib/utils'
import { MonitorCog } from 'lucide-react'

export default function StartSimulationButton({
  currentRoom,
}: {
  currentRoom: Room
}) {
  const isNotReady = !currentRoom?.is_ready_for_simulation
  const dispatch = useAppDispatch()
  const { roomid, scenario_title } = currentRoom
  const isGameCompleted = currentRoom?.status === GameRoomStatus.COMPLETED
  const isSimulating = currentRoom?.is_simulating === true
  const [isStarting, setIsStarting] = useState(false)

  const handleStartSimulation = useCallback(async () => {
    if (
      isNotReady ||
      currentRoom?.status === GameRoomStatus.IN_PROGRESS ||
      isGameCompleted ||
      isStarting ||
      isSimulating
    ) {
      return
    }

    try {
      setIsStarting(true)

      await dispatch(
        updateRoomDetails({
          roomId: roomid,
          scenario_title: scenario_title!,
          status: GameRoomStatus.IN_PROGRESS,
          start_time: new Date(),
          is_simulating: true,
        })
      ).unwrap()

      await dispatch(fetchTimerConfigurationThunk(roomid)).unwrap()

      await dispatch(startNextRoundThunk(roomid)).unwrap()

      await dispatch(getRoomDetails(roomid)).unwrap()

      toast.success('Pilot started successfully')
    } catch (error) {
      console.error('Failed to start pilot:', error)
      toast.error('Failed to start pilot. Please try again.')
    } finally {
      setIsStarting(false)
    }
  }, [
    isNotReady,
    currentRoom?.status,
    isGameCompleted,
    isStarting,
    isSimulating,
    dispatch,
    roomid,
    scenario_title,
  ])

  const isButtonDisabled = useMemo(
    () =>
      isNotReady ||
      isGameCompleted ||
      isStarting ||
      currentRoom.status === GameRoomStatus.IN_PROGRESS ||
      currentRoom.status === GameRoomStatus.PAUSED ||
      isSimulating,
    [isNotReady, isGameCompleted, isStarting, currentRoom.status, isSimulating]
  )

  const buttonText = useMemo(() => {
    if (isStarting) {
      return 'Starting Pilot...'
    }
    if (isSimulating) {
      return 'Pilot in Progress'
    }
    if (currentRoom?.status === GameRoomStatus.NOT_STARTED) {
      return 'Start Pilot'
    }
    return 'Pilot'
  }, [isStarting, isSimulating, currentRoom?.status])

  return (
    <div className='flex flex-col'>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className='inline-block'>
              <Button
                className={cn(
                  'flex gap-2 items-center transition-all',
                  isButtonDisabled
                    ? 'opacity-50 cursor-not-allowed bg-disabled hover:bg-disabled-hovered text-zinc-500'
                    : 'text-white bg-purple-600 hover:bg-purple-700'
                )}
                onClick={handleStartSimulation}
                disabled={isButtonDisabled}
              >
                <MonitorCog className='h-[14.4px] w-[14.4px]' />
                <span className='text-base'>{buttonText}</span>
              </Button>
            </div>
          </TooltipTrigger>
          {isButtonDisabled && (
            <TooltipContent side='top'>
              {isNotReady && (
                <p>
                  Add at least one player, add one event, and assign teams to
                  all players to start the pilot. During the pilot,
                  players are used regardless of invitation status.
                  {currentRoom?.game_mode ===
                    TimerMode.SINGLE_ROUND_ADVISOR_ONLY && (
                    <>
                      <br />
                      Assigning at least one advisor to every player is
                      mandatory.
                    </>
                  )}
                </p>
              )}
              {currentRoom?.status === GameRoomStatus.IN_PROGRESS && (
                <p>The game is already in progress.</p>
              )}
              {isSimulating && <p>Pilot is already in progress.</p>}
              {isGameCompleted && (
                <p>This game has ended and cannot be restarted.</p>
              )}
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>
    </div>
  )
}
